import { CloudFrontCookiesKey, CloudfrontSignedCookiesOutput } from '@/types';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useCookies } from 'react-cookie';
import { CookieSetOptions } from 'universal-cookie';
import * as Sentry from '@sentry/browser';
/** remove leading and trailing white spaces and line terminators and also any leading slashes (/) from input string */
const removeLeadingSlashes = (input: string) => (input ?? '').trim().replace(/^\/+/, '');

export const getCloudfrontUrl = (filepath: string) => {
  const relativePathEncoded = removeLeadingSlashes(filepath);

  try {
    return new URL(relativePathEncoded, import.meta.env.VITE_APP_CDN_BASE_URL).href;
  } catch (error) {
    console.error('cloudfront.ts -> getCloudfrontUrl() ->', error);
    console.error('cloudfront.ts -> getCloudfrontUrl() -> filePath ->', filepath);
    return `${import.meta.env.VITE_APP_CDN_BASE_URL}/${relativePathEncoded}`;
  }
};

export const useFetchCloudfrontSignedCookies = () => {
  const getCloudfrontSignedCookieOptions = useCallback(
    (path?: string): CookieSetOptions => ({
      httpOnly: false,
      secure: true,
      sameSite: 'lax',
      // NOTE: must ensure domain for the cookie is a subdomain for both cloudfront CDN & frontend or browser rejects
      // see: https://stackoverflow.com/questions/41990841/how-to-use-cloudfront-signed-cookies-in-the-browser
      // and see https://stackoverflow.com/a/30384487
      domain: import.meta.env.VITE_APP_CDN_BASE_URL.split('.').slice(1).join('.'),
      ...(path && { path }),
    }),
    []
  );

  const { getAccessTokenSilently, user } = useAuth0();
  const cookieNames = Object.values(CloudFrontCookiesKey);
  const [, setCookie] = useCookies(cookieNames);

  const fetchCookies = useCallback(async () => {
    let cookies: Record<string, CloudfrontSignedCookiesOutput> = {};
    try {
      const token = await getAccessTokenSilently();
      cookies = await (
        await fetch(`${import.meta.env.VITE_APP_API_URL}/cloudfront-cookies`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        })
      ).json();

      const firstCookie = Object.values(cookies)[0];

      setCookie(
        CloudFrontCookiesKey.KeyPair,
        firstCookie['CloudFront-Key-Pair-Id'],
        getCloudfrontSignedCookieOptions('/')
      );

      Object.entries(cookies).forEach(([path, cookie]) => {
        // individual policy / signature cookies scoped per base CDN path
        setCookie(
          CloudFrontCookiesKey.Policy,
          cookie['CloudFront-Policy'],
          getCloudfrontSignedCookieOptions(path)
        );
        setCookie(
          CloudFrontCookiesKey.Signature,
          cookie['CloudFront-Signature'],
          getCloudfrontSignedCookieOptions(path)
        );
      });

      return cookies;
    } catch {
      return {};
    }
  }, [getAccessTokenSilently, getCloudfrontSignedCookieOptions, setCookie]);
  const { VITE_APP_API_URL, NODE_ENV } = import.meta.env;
  const setCookies = useCallback(async () => {
    try {
      // use fallback method for local development
      if (VITE_APP_API_URL.includes('localhost') || NODE_ENV === 'test') {
        return fetchCookies();
      }
      const token = await getAccessTokenSilently();
      const response = await fetch(`${import.meta.env.VITE_APP_API_URL}/cloudfront-cookies-set`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });

      return response;
    } catch {
      // use fallback method for local development
      Sentry.captureMessage(`Failed to call cloudfront-cookies-set`, {
        user: { sub: user?.sub },
      });

      return fetchCookies();
    }
  }, [getAccessTokenSilently, fetchCookies, user?.sub]);
  return { fetchCookies, setCookies };
};
